<ng-container *transloco="let t">
  <div class="justify-content-space-between">
    <form [formGroup]="form" class="report-filters">
      <mat-form-field
        appearance="outline"
        class="report-filters__search hidden-error"
        subscriptSizing="dynamic"
      >
        <input
          matInput
          formControlName="search"
          type="text"
          [placeholder]="t('Search reports by number, veterinarian or clinic...')"
          [attr.aria-label]="t('Search reports by number, veterinarian or clinic...')"
        />
        @if (form.controls.search.value?.length) {
          <button
            matSuffix
            mat-icon-button
            type="button"
            (click)="clearSearch()"
            [attr.aria-label]="t('Clear')"
          >
            <mat-icon
              class="close-icon icon-xs cursor"
              style="width: 16px; height: 16px"
            ></mat-icon>
          </button>
        }
      </mat-form-field>

      <mat-form-field
        appearance="outline"
        class="report-filters__vet hidden-error"
        subscriptSizing="dynamic"
      >
        <mat-label>{{ t('Veterinarians') }}</mat-label>
        <mat-select [formControl]="veterinarian">
          <mat-option [value]="undefined">{{ t('All') }}</mat-option>
          @for (veterinarian of store.veterinarians(); track veterinarian) {
            <mat-option [value]="veterinarian.uid">
              {{ veterinarian.firstName + ' ' + veterinarian.lastName }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field
        appearance="outline"
        class="report-filters__date hidden-error"
        subscriptSizing="dynamic"
      >
        <mat-date-range-input [formGroup]="range" [rangePicker]="picker">
          <input
            matStartDate
            formControlName="start"
            [placeholder]="t('Start date')"
          />
          <input
            matEndDate
            formControlName="end"
            [placeholder]="t('End date')"
          />
        </mat-date-range-input>

        <mat-date-range-picker #picker></mat-date-range-picker>

        @if (range.controls.start.hasError('matStartDateInvalid')) {
          <mat-error>{{ t('Invalid start date') }}</mat-error>
        }
        @if (range.controls.end.hasError('matEndDateInvalid')) {
          <mat-error>{{ t('Invalid end date') }}</mat-error>
        }
        <div class="report-filters__date-actions">
          @if (range.value.start && range.value.end) {
            <button
              matIconSuffix
              mat-icon-button
              (click)="clearRange()"
              aria-label="Clear date range"
            >
              <mat-icon
                class="close-icon icon-xs cursor"
                style="width: 16px; height: 16px"
              ></mat-icon>
            </button>
          }

          <mat-datepicker-toggle
            matIconSuffix
            [for]="picker"
          ></mat-datepicker-toggle>
        </div>
      </mat-form-field>
    </form>
  </div>

  <div class="mx-4 sm:-mx-0">
    <div class="overflow-hidden sm:rounded-lg">
      <div
        #tableWrapper
        class="table-wrapper table-wrapper--mobile-cards"
        style="position: relative"
        infiniteScroll
        [infiniteScrollDistance]="2"
        [infiniteScrollThrottle]="100"
        [scrollWindow]="false"
        (scrolled)="loadMore()"
      >
        <anihis-loading-overlay
          [isLoading]="store.statusSearch() === status.Loading"
          [size]="50"
        />

        @defer (when store.reports()) {
          @if (store.reports()?.length! > 0) {
            <div class="mobile-card-list">
              @for (property of store.reports(); track property.healthRecordUid) {
                <anihis-admin-item-mobile-card
                  [title]="property.healthRecordNumber || '—'"
                  [subtitle]="property.clinicName || ''"
                  [fields]="[
                    {
                      label: t('Veterinarian licence number'),
                      value: property.licenceNumber,
                    },
                    {
                      label: t('Report created date'),
                      value: property.createDateTimeUtc | localDate,
                    },
                    {
                      label: t('Report last modified date'),
                      value:
                        property.lastModifiedDateTimeUtc | localDate,
                    },
                  ]"
                  [showDelete]="false"
                  [showEdit]="canManageVisibility(property)"
                  [showPreview]="true"
                  (edit)="openVisibilityDialog(property)"
                  (preview)="togglePreviewCard(property.healthRecordUid, property)"
                />
              }
            </div>

            <mat-table class="min-w-max desktop-only-table" [dataSource]="store.reports()!">
              <ng-container matColumnDef="Health Record Number">
                <mat-header-cell
                  *matHeaderCellDef
                  class="custom-header"
                  mat-header-cell
                  [matTooltip]="t('Report number')"
                  matTooltipShowDelay="400"
                >
                  <span>{{ t('Report number') }}</span>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property"
                  mat-cell
                  class="flex flex-row gap-1 items-center"
                  [class.report-row--restricted]="property.canView === false"
                >
                  @if (property.canView === false) {
                    <mat-icon
                      class="report-lock-icon icon-xs"
                      [matTooltip]="t('Report access restricted')"
                      matTooltipShowDelay="200"
                    >lock</mat-icon>
                  }
                  <span
                    [innerHTML]="
                      property.healthRecordNumber | emptyFallbackHtml
                    "
                  ></span>
                </mat-cell>
              </ng-container>

              <ng-container matColumnDef="Name Of Clinic" sticky>
                <mat-header-cell
                  *matHeaderCellDef
                  class="custom-header"
                  mat-header-cell
                  [matTooltip]="t('Report clinic name')"
                  matTooltipShowDelay="400"
                >
                  <span>{{ t('Report clinic name') }}</span>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property"
                  mat-cell
                  class="flex flex-row gap-1"
                >
                  <mat-chip class="chip-grey">
                    <div class="flex gap-s">
                      @if (property?.profilePicture) {
                        <img (click)="previewImage(property.profilePicture?.url!)"
                          style="
                            height: 24px;
                            width: 24px;
                            border-radius: 50%;
                            cursor: pointer;
                          "
                          [src]="property?.profilePicture?.thumbnailUrl" alt="" />
                      }
                      <span
                        class="truncate max-w-[220px] block"
                        [matTooltip]="property.clinicName"
                        matTooltipShowDelay="200"
                        matTooltipPosition="above"
                        >{{ property.clinicName }}</span
                      >
                    </div>
                  </mat-chip>
                </mat-cell>
              </ng-container>

              <ng-container matColumnDef="License Number">
                <mat-header-cell
                  *matHeaderCellDef
                  class="custom-header"
                  mat-header-cell
                  [matTooltip]="t('Veterinarian licence number')"
                  matTooltipShowDelay="400"
                >
                  <span>{{ t('Veterinarian licence number') }}</span>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property"
                  mat-cell
                  class="flex flex-row gap-1"
                >
                  <span
                    [innerHTML]="property.licenceNumber | emptyFallbackHtml"
                  ></span>
                </mat-cell>
              </ng-container>

              <ng-container matColumnDef="Creation Date">
                <mat-header-cell
                  *matHeaderCellDef
                  class="custom-header text-right"
                  mat-header-cell
                  [matTooltip]="t('Report created date')"
                  matTooltipShowDelay="400"
                >
                  <span>{{ t('Report created date') }}</span>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property"
                  mat-cell
                  class="flex flex-row gap-1"
                >
                  <span
                    [innerHTML]="
                      property.createDateTimeUtc
                        | localDate
                        | emptyFallbackHtml
                    "
                  ></span>
                </mat-cell>
              </ng-container>

              <ng-container matColumnDef="Last Modified Date">
                <mat-header-cell
                  *matHeaderCellDef
                  class="custom-header text-right"
                  mat-header-cell
                  [matTooltip]="t('Report last modified date')"
                  matTooltipShowDelay="400"
                >
                  <span>{{ t('Report last modified date') }}</span>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property"
                  mat-cell
                  class="flex flex-row gap-1"
                >
                  <span
                    [innerHTML]="
                      property.lastModifiedDateTimeUtc
                        | localDate
                        | emptyFallbackHtml
                    "
                  ></span>
                </mat-cell>
              </ng-container>

              <ng-container matColumnDef="Actions" stickyEnd>
                <mat-header-cell *matHeaderCellDef mat-header-cell>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property; let i = index"
                  mat-cell
                  class="flex flex-row gap-1"
                  [ngClass]="{
                    'sticky-actions-last':
                      i === (store.reports()?.length ?? 0) - 1
                  }"
                >
                  <div class="report-actions">
                    <div class="report-actions__slot">
                      @if (canManageVisibility(property)) {
                        <button
                          [matTooltip]="t('Report visibility')"
                          matTooltipShowDelay="200"
                          matTooltipPosition="above"
                          (click)="openVisibilityDialog(property)"
                          class="flex items-center justify-center"
                          [attr.aria-label]="t('Report visibility')"
                        >
                          <mat-icon class="edit-white-icon icon-s"></mat-icon>
                        </button>
                      }
                    </div>

                    <div class="report-actions__slot">
                      @if (canManageVisibility(property)) {
                        <button
                          [matTooltip]="t('Send PDF to owner')"
                          matTooltipShowDelay="200"
                          matTooltipPosition="above"
                          (click)="
                            ($event.stopPropagation);
                            sendReportEmailToOwner(property)
                          "
                          class="flex items-center justify-center"
                          [disabled]="sendingEmailUid() === property.healthRecordUid"
                          [attr.aria-label]="t('Send PDF to owner')"
                        >
                          <mat-icon class="email-white-icon icon-s"></mat-icon>
                        </button>
                      }
                    </div>

                    <div class="report-actions__slot">
                      <button
                        [matTooltip]="t('Preview detail report')"
                        matTooltipShowDelay="200"
                        matTooltipPosition="above"
                        (click)="
                          ($event.stopPropagation);
                          togglePreviewCard(property.healthRecordUid, property)
                        "
                        class="flex items-center justify-center"
                        [attr.aria-label]="t('Preview detail report')"
                      >
                        <mat-icon class="preview-white-icon icon-s"></mat-icon>
                      </button>
                    </div>
                  </div>
                </mat-cell>
              </ng-container>

              <!-- Row Definitions -->
              <mat-header-row
                *matHeaderRowDef="displayedColumns; sticky: true"
              ></mat-header-row>

              <mat-row
                *matRowDef="let row; columns: displayedColumns"
              ></mat-row>
            </mat-table>

            @if (
              store.status() === status.Loading &&
              store.statusSearch() !== status.Loading
            ) {
              <div class="centered-message mt-4">
                <anihis-loading-spinner
                  [size]="'small'"
                ></anihis-loading-spinner>
              </div>
            }
          } @else {
            <div class="centered-message mt-4">
              {{ t('You have no reports') }}
            </div>
          }
        } @placeholder {
          <div class="scroll-hidden">
            <anihis-loading-skeleton [height]="'32dvh'" [minWidth]="'100%'" />
          </div>
        }
      </div>
    </div>
  </div>
</ng-container>
